/**
 * The thumbnail strip for one report, and the lightbox behind it.
 *
 * Per `budgets-draws-report-photos.md` §Behaviour:
 *  - thumbnails in REPORT PAGE ORDER, lazy-loaded, fixed height, object-fit
 *    cover, `label` beneath and a `stage` badge on the tile
 *  - category filter chips, with `document_page` and `other` hidden by default
 *  - a click opens the lightbox: full image, label, interpretation, workVisible,
 *    stage, and the linked budget lines as chips; left/right move through the
 *    SAME report's photos
 *  - when `siteImagesStatus` is `no_site_images_included` / `no_images` the strip
 *    is replaced by a single neutral line — information, not an error — and a
 *    feasibility that was a desk review says so instead
 *
 * A photo with no `imagePath` was catalogued but never stored (a logo, a cover
 * page); the doc says not to render a thumbnail for it, so those are dropped
 * from the strip entirely rather than shown as broken tiles.
 */
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ImageNotSupportedOutlined from "@mui/icons-material/ImageNotSupportedOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { T } from "./budgetDrawsTheme";
import { DESK_REVIEW_LINE } from "./feasibilityModel";
import {
  DEFAULT_HIDDEN_CATEGORIES,
  hasImage,
  orderedPhotos,
  PHOTO_CATEGORY_LABEL,
  PHOTO_STAGE_LABEL,
  PHOTO_STAGE_TONE,
  useNearViewport,
  useReportPhotoUrl,
} from "./reportPhotosModel";

const THUMB_W = 148;
const THUMB_H = 104;

const STAGE_TONE = {
  good: { bg: T.greenTint, ink: T.green, line: T.greenLine },
  info: { bg: T.accentTint, ink: T.accentInk, line: T.accentTint2 },
  neutral: { bg: "rgba(24,36,48,.72)", ink: "#fff", line: "transparent" },
};

/** One lazily-loaded image. Renders a skeleton until its bytes arrive. */
const PhotoImage = ({ api, dealId, photo, height, fit = "cover", eager }) => {
  const boxRef = useRef(null);
  const near = useNearViewport(boxRef);
  const { url, failed } = useReportPhotoUrl(
    api,
    dealId,
    photo?.imagePath,
    eager || near,
  );
  return (
    <Box
      ref={boxRef}
      sx={{
        width: "100%",
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: T.line2,
        overflow: "hidden",
      }}
    >
      {url ? (
        <Box
          component="img"
          src={url}
          alt={photo?.label || "Report photo"}
          loading="lazy"
          sx={{ width: "100%", height: "100%", objectFit: fit, display: "block" }}
        />
      ) : failed ? (
        <ImageNotSupportedOutlined sx={{ fontSize: 20, color: T.faint }} />
      ) : (
        <CircularProgress size={16} thickness={5} sx={{ color: T.faint }} />
      )}
    </Box>
  );
};

const StageBadge = ({ stage, floating }) => {
  if (!stage) return null;
  const c = STAGE_TONE[PHOTO_STAGE_TONE[stage] || "neutral"];
  return (
    <Box
      sx={{
        px: 0.75,
        py: "1px",
        borderRadius: "5px",
        fontSize: 10.5,
        fontWeight: 600,
        background: floating ? STAGE_TONE.neutral.bg : c.bg,
        color: floating ? "#fff" : c.ink,
        border: floating ? "none" : `1px solid ${c.line}`,
        whiteSpace: "nowrap",
      }}
    >
      {PHOTO_STAGE_LABEL[stage] || stage}
    </Box>
  );
};

/* ── lightbox ────────────────────────────────────────────────────────────── */

/**
 * @param {object[]} items  [{ review, photo }] — the set the arrows move through.
 *   One report's photos when opened from a strip; one budget line's photos
 *   across every report (oldest first) when opened from the budget grid.
 */
export const ReportPhotoLightbox = ({
  api,
  dealId,
  items,
  index,
  onIndex,
  onClose,
  onOpenLine,
  onPreviewReport,
  title,
}) => {
  const current = items?.[index];
  const photo = current?.photo;
  const review = current?.review;

  const step = useCallback(
    (delta) => {
      if (!items?.length) return;
      onIndex((index + delta + items.length) % items.length);
    },
    [items, index, onIndex],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  if (!photo) return null;

  const facts = [
    ["Work visible", photo.workVisible],
    ["Category", PHOTO_CATEGORY_LABEL[photo.category] || photo.category],
    ["Report page", photo.page != null ? `Page ${photo.page}` : null],
    ["Vendor", review?.reportVendor],
    ["Source", photo.reusedFromAppraisal === true ? "One of the appraisal's images" : null],
  ].filter(([, v]) => v);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <Flx
        ac
        jb
        g={2}
        sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${T.line}` }}
      >
        <Flx ac g={1.25} sx={{ minWidth: 0 }}>
          <Txt sx={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
            {photo.label || "Report photo"}
          </Txt>
          <StageBadge stage={photo.stage} />
          {photo.matchesSubject === false && (
            <Tooltip title="This photo may not be the subject property.">
              <Flx
                ac
                g={0.5}
                sx={{
                  px: 0.875,
                  py: "2px",
                  borderRadius: "5px",
                  background: T.amberTint,
                  border: `1px solid ${T.amberLine}`,
                }}
              >
                <WarningAmberRounded sx={{ fontSize: 13, color: T.amberInk }} />
                <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: T.amberInk }}>
                  May not be the subject
                </Txt>
              </Flx>
            </Tooltip>
          )}
        </Flx>
        <Flx ac g={0.5}>
          <Txt sx={{ fontSize: 12.5, color: T.muted, whiteSpace: "nowrap" }}>
            {index + 1} of {items.length}
            {title ? ` · ${title}` : ""}
          </Txt>
          <IconButton size="small" onClick={() => step(-1)} aria-label="Previous photo">
            <ChevronLeftRounded />
          </IconButton>
          <IconButton size="small" onClick={() => step(1)} aria-label="Next photo">
            <ChevronRightRounded />
          </IconButton>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseRounded />
          </IconButton>
        </Flx>
      </Flx>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
        }}
      >
        <Box sx={{ background: "#0F1720" }}>
          <PhotoImage
            api={api}
            dealId={dealId}
            photo={photo}
            height={520}
            fit="contain"
            eager
          />
        </Box>

        <Box sx={{ px: 2.5, py: 2.25, borderLeft: `1px solid ${T.line}` }}>
          {photo.interpretation && (
            <Txt
              component="div"
              sx={{ fontSize: 13, color: T.ink2, lineHeight: 1.6, mb: 2 }}
            >
              {photo.interpretation}
            </Txt>
          )}

          {facts.map(([label, value]) => (
            <Flx
              key={label}
              ac
              jb
              g={2}
              sx={{ py: 0.75, borderTop: `1px solid ${T.line2}` }}
            >
              <Txt sx={{ fontSize: 12.5, color: T.muted }}>{label}</Txt>
              <Txt
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: T.ink,
                  textAlign: "right",
                }}
              >
                {value}
              </Txt>
            </Flx>
          ))}

          {/* Budget lines this photo evidences. The chip navigates by
              lineItemIds — the printed names are display only, and a name that
              didn't resolve to a line on this loan's sheet has no id, so it
              renders as a plain (unclickable) chip. */}
          {(photo.budgetLines?.length > 0 || photo.lineItemIds?.length > 0) && (
            <Box sx={{ mt: 2 }}>
              <Txt
                component="div"
                sx={{ fontSize: 12, fontWeight: 600, color: T.muted, mb: 0.875 }}
              >
                Budget lines evidenced
              </Txt>
              <Flx ac g={0.625} sx={{ flexWrap: "wrap" }}>
                {(photo.budgetLines || []).map((name, i) => {
                  const id = photo.lineItemIds?.[i]
                    ? String(photo.lineItemIds[i])
                    : null;
                  const clickable = Boolean(id && onOpenLine);
                  return (
                    <Box
                      key={`${name}-${i}`}
                      component={clickable ? "button" : "span"}
                      type={clickable ? "button" : undefined}
                      onClick={
                        clickable
                          ? () => {
                              onOpenLine(id);
                              onClose();
                            }
                          : undefined
                      }
                      sx={{
                        px: 1,
                        py: "3px",
                        borderRadius: "6px",
                        border: `1px solid ${clickable ? T.accentTint2 : T.line}`,
                        background: clickable ? T.accentTint : "#fff",
                        color: clickable ? T.accentInk : T.muted,
                        fontFamily: "inherit",
                        fontSize: 11.5,
                        fontWeight: 600,
                        cursor: clickable ? "pointer" : "default",
                        "&:hover": clickable
                          ? { background: T.accentTint2 }
                          : undefined,
                      }}
                    >
                      {name}
                    </Box>
                  );
                })}
              </Flx>
            </Box>
          )}

          {photo.sourceCaption && (
            <Txt
              component="div"
              sx={{ fontSize: 12, color: T.faint, mt: 2, lineHeight: 1.55 }}
            >
              Caption in report: {photo.sourceCaption}
            </Txt>
          )}

          {photo.page != null && onPreviewReport && (
            <Box
              component="button"
              type="button"
              onClick={() => onPreviewReport(review, photo.page)}
              sx={{
                mt: 2,
                border: 0,
                background: "none",
                p: 0,
                fontFamily: "inherit",
                fontSize: 12.5,
                fontWeight: 600,
                color: T.accentInk,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Open report at page {photo.page}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

/* ── strip ───────────────────────────────────────────────────────────────── */

const ReportPhotoStrip = ({ api, dealId, review, onOpenLine, onPreviewReport }) => {
  const [hidden, setHidden] = useState(DEFAULT_HIDDEN_CATEGORIES);
  const [openIdx, setOpenIdx] = useState(null);

  // A catalogued-but-unstored photo (logo / cover page) gets no tile at all.
  const photos = useMemo(
    () => orderedPhotos(review).filter(hasImage),
    [review],
  );

  const categories = useMemo(() => {
    const counts = new Map();
    photos.forEach((p) => {
      const c = p.category || "other";
      counts.set(c, (counts.get(c) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [photos]);

  const visible = useMemo(
    () => photos.filter((p) => !hidden.includes(p.category || "other")),
    [photos, hidden],
  );

  const items = useMemo(
    () => visible.map((photo) => ({ review, photo })),
    [visible, review],
  );

  const toggle = (cat) =>
    setHidden((h) => (h.includes(cat) ? h.filter((c) => c !== cat) : [...h, cat]));

  // "No site photos": a neutral line replaces the strip. Information, not an
  // error — and a desk-review feasibility says which kind of nothing it is.
  const noImages =
    review?.siteImagesStatus === "no_images" ||
    review?.siteImagesStatus === "no_site_images_included";
  if (noImages || photos.length === 0) {
    return (
      <Txt sx={{ fontSize: 12.5, color: T.muted, lineHeight: 1.5 }}>
        {review?.reportKind === "feasibility" &&
        review?.reviewBasis === "desk_review_no_site_visit"
          ? DESK_REVIEW_LINE
          : "No site images included in this report."}
      </Txt>
    );
  }

  return (
    <Box>
      {categories.length > 1 && (
        <Flx ac g={0.625} sx={{ mb: 1.25, flexWrap: "wrap" }}>
          {categories.map(([cat, n]) => {
            const on = !hidden.includes(cat);
            return (
              <Box
                key={cat}
                component="button"
                type="button"
                onClick={() => toggle(cat)}
                aria-pressed={on}
                sx={{
                  px: 1,
                  py: "3px",
                  borderRadius: "999px",
                  border: `1px solid ${on ? T.accentTint2 : T.line}`,
                  background: on ? T.accentTint : "#fff",
                  color: on ? T.accentInk : T.faint,
                  fontFamily: "inherit",
                  fontSize: 11.5,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {PHOTO_CATEGORY_LABEL[cat] || cat} {n}
              </Box>
            );
          })}
        </Flx>
      )}

      <Flx
        g={1}
        sx={{
          overflowX: "auto",
          pb: 0.5,
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: T.line,
            borderRadius: 3,
          },
        }}
      >
        {visible.map((photo, i) => (
          <Box
            key={`${photo.index}-${photo.imagePath}`}
            component="button"
            type="button"
            onClick={() => setOpenIdx(i)}
            sx={{
              flex: "0 0 auto",
              width: THUMB_W,
              p: 0,
              border: `1px solid ${T.line}`,
              borderRadius: T.radiusSm,
              background: "#fff",
              overflow: "hidden",
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "inherit",
              "&:hover": { borderColor: T.accent },
            }}
          >
            <Box sx={{ position: "relative" }}>
              <PhotoImage
                api={api}
                dealId={dealId}
                photo={photo}
                height={THUMB_H}
              />
              {photo.stage && (
                <Box sx={{ position: "absolute", left: 6, bottom: 6 }}>
                  <StageBadge stage={photo.stage} floating />
                </Box>
              )}
              {photo.reusedFromAppraisal === true && (
                <Tooltip title="This photo is one of the appraisal's images">
                  <Box
                    sx={{
                      position: "absolute",
                      left: 6,
                      top: 6,
                      px: 0.75,
                      py: "1px",
                      borderRadius: "5px",
                      fontSize: 10.5,
                      fontWeight: 600,
                      background: STAGE_TONE.neutral.bg,
                      color: "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    from appraisal
                  </Box>
                </Tooltip>
              )}
              {photo.matchesSubject === false && (
                <Tooltip title="May not be the subject property">
                  <WarningAmberRounded
                    sx={{
                      position: "absolute",
                      right: 6,
                      top: 6,
                      fontSize: 16,
                      color: T.amber,
                      filter: "drop-shadow(0 1px 2px rgba(0,0,0,.45))",
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            <Txt
              component="div"
              sx={{
                px: 0.875,
                py: 0.75,
                fontSize: 11.5,
                color: T.ink2,
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minHeight: 34,
              }}
            >
              {photo.label || `Page ${photo.page ?? "?"}`}
            </Txt>
          </Box>
        ))}
      </Flx>

      {openIdx != null && items[openIdx] && (
        <ReportPhotoLightbox
          api={api}
          dealId={dealId}
          items={items}
          index={openIdx}
          onIndex={setOpenIdx}
          onClose={() => setOpenIdx(null)}
          onOpenLine={onOpenLine}
          onPreviewReport={onPreviewReport}
        />
      )}
    </Box>
  );
};

ReportPhotoStrip.propTypes = {
  api: PropTypes.object.isRequired,
  dealId: PropTypes.string,
  review: PropTypes.object,
  /** (lineItemId) => void — scroll to + highlight that budget row. */
  onOpenLine: PropTypes.func,
  /** (review, page) => void — open the source report document. */
  onPreviewReport: PropTypes.func,
};

export default ReportPhotoStrip;
